/**
 * Stream Service - API client and utilities for live streaming
 * Based on browser-stream-example.js
 */

/* global VideoEncoder, AudioEncoder, MediaStreamTrackProcessor, AudioData */


// =============================================================================
// CONFIGURATION
// =============================================================================

// Video codec options with recommended settings
export const VIDEO_CODEC_OPTIONS = {
  // H.264 codecs
  'avc1.640028': { name: 'H.264 High (1080p)', type: 'h264', level: '4.0', width: 1920, height: 1080, bitrate: 4000000 },
  'avc1.4D0028': { name: 'H.264 Main (1080p)', type: 'h264', level: '4.0', width: 1920, height: 1080, bitrate: 4000000 },
  'avc1.64001F': { name: 'H.264 High (720p)', type: 'h264', level: '3.1', width: 1280, height: 720, bitrate: 2000000 },
  // HEVC codecs
  'hvc1.1.6.L120.B0': { name: 'HEVC Main (1080p)', type: 'hevc', level: '4.0', width: 1920, height: 1080, bitrate: 3000000 },
  'hvc1.1.6.L93.B0': { name: 'HEVC Main (720p)', type: 'hevc', level: '3.1', width: 1280, height: 720, bitrate: 1500000 },
};

export const STREAM_CONFIG = {
  apiBaseUrl: 'https://hb.ermis.network/stream-gate',
  
  // Dynamic: reads from localStorage (set during login)
  get appName() {
    try {
      const info = JSON.parse(localStorage.getItem('stream_info') || 'null');
      return info?.app_name || 'Streaming-demo';
    } catch {
      return 'Streaming-demo';
    }
  },
  
  // Dynamic: stream token from login response
  get streamToken() {
    return localStorage.getItem('streamToken') || '';
  },
  
  video: {
    codec: 'avc1.4D0028',  // H.264 Main Profile (1080p) - default
    width: 1920,
    height: 1080,
    bitrate: 4000000,      // 4 Mbps for 1080p
    framerate: 30,
    keyframeInterval: 60,  // Keyframe every 2 seconds at 30fps
  },
  
  audio: {
    codec: 'mp4a.40.2',    // AAC-LC
    sampleRate: 48000,
    channels: 2,
    bitrate: 128000,       // 128 kbps
  },
};

/**
 * Get recommended settings when codec changes
 * @param {string} codec - The video codec string
 * @returns {object} - Recommended width, height, bitrate
 */
export function onCodecChange(codec) {
  const codecInfo = VIDEO_CODEC_OPTIONS[codec];
  if (codecInfo) {
    return {
      width: codecInfo.width,
      height: codecInfo.height,
      bitrate: codecInfo.bitrate,
      type: codecInfo.type,
      name: codecInfo.name,
    };
  }
  // Default fallback
  return {
    width: 1920,
    height: 1080,
    bitrate: 4000000,
    type: 'h264',
    name: 'Unknown',
  };
}

/**
 * Check if codec is HEVC
 */
export function isHevcCodec(codec) {
  return codec.startsWith('hvc1') || codec.startsWith('hev1');
}

// =============================================================================
// STREAM API CLIENT
// =============================================================================

export class StreamApiClient {
  constructor(baseUrl = STREAM_CONFIG.apiBaseUrl, token = null) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this._token = token;
  }

  /**
   * Get the current token - uses provided token or falls back to STREAM_CONFIG
   */
  get token() {
    return this._token || STREAM_CONFIG.streamToken;
  }

  setToken(token) {
    this._token = token;
  }

  getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  /**
   * Create a new stream (server defaults to browser method)
   */
  async createStream(streamName) {
    const response = await fetch(`${this.baseUrl}/streams`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        app_name: STREAM_CONFIG.appName,
        stream_name: streamName,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create stream: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[StreamAPI] Stream created:', data);
    return data;
  }

  /**
   * Activate the stream (set is_live = true)
   */
  async activateStream(streamId) {
    const response = await fetch(`${this.baseUrl}/streams/${streamId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ is_live: true }),
    });

    if (!response.ok) {
      throw new Error(`Failed to activate stream: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[StreamAPI] Stream activated:', data);
    return data;
  }

  /**
   * Deactivate the stream (set is_live = false)
   */
  async deactivateStream(streamId) {
    const response = await fetch(`${this.baseUrl}/streams/${streamId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify({ is_live: false }),
    });

    if (!response.ok) {
      throw new Error(`Failed to deactivate stream: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get stream details
   */
  async getStream(streamId) {
    const response = await fetch(`${this.baseUrl}/streams/${streamId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get stream: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get viewer count for a stream
   */
  async getViewerCount(streamId) {
    try {
      const response = await fetch(
        `${this.baseUrl}/hls/${STREAM_CONFIG.appName}/${streamId}/viewers`,
        { method: 'GET', headers: this.getHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        return data.viewers || data.count || 0;
      }
      return 0;
    } catch (e) {
      console.warn('[StreamAPI] Failed to get viewer count:', e);
      return 0;
    }
  }

  /**
   * Build publish WebSocket URL
   */
  getPublishWsUrl(streamId) {
    const wsBase = this.baseUrl
      .replace('http://', 'ws://')
      .replace('https://', 'wss://');
    return `${wsBase}/browser/publish/${STREAM_CONFIG.appName}/${streamId}`;
  }

  /**
   * Build HLS playlist URL
   */
  getHlsUrl(streamId) {
    return `${this.baseUrl}/browser/hls/${streamId}/playlist.m3u8`;
  }

  /**
   * Build simple HLS playlist URL (for basic players)
   */
  getSimpleHlsUrl(streamId) {
    return `${this.baseUrl}/browser/hls/${streamId}/simple.m3u8`;
  }
}

// =============================================================================
// BROWSER PUBLISHER
// =============================================================================

export class BrowserPublisher {
  constructor() {
    this.ws = null;
    this.videoEncoder = null;
    this.audioEncoder = null;
    this.mediaStream = null;
    this.isPublishing = false;
    this.startTimestamp = 0;
    this.stats = { videoFrames: 0, audioFrames: 0, keyframes: 0, bytesSent: 0 };
    this.onStats = null;
    this._facingMode = 'environment'; // 'user' for front camera
  }

  /**
   * Start publishing to the server
   */
  async startPublishing(wsUrl) {
    console.log('[Publisher] Starting publishing to:', wsUrl);

    // Capture camera and microphone (use ideal constraints so mobile picks best resolution)
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: STREAM_CONFIG.video.width },
        height: { ideal: STREAM_CONFIG.video.height },
        frameRate: { ideal: STREAM_CONFIG.video.framerate },
        facingMode: this._facingMode,
      },
      audio: {
        sampleRate: STREAM_CONFIG.audio.sampleRate,
        channelCount: STREAM_CONFIG.audio.channels,
      },
    });

    // Read actual camera dimensions (mobile may differ from requested)
    const videoTrack = this.mediaStream.getVideoTracks()[0];
    const trackSettings = videoTrack.getSettings();
    this._actualWidth = trackSettings.width || STREAM_CONFIG.video.width;
    this._actualHeight = trackSettings.height || STREAM_CONFIG.video.height;
    console.log(`[Publisher] Media stream acquired: actual ${this._actualWidth}x${this._actualHeight}`);

    // Connect to WebSocket
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(wsUrl);
      this.ws.binaryType = 'arraybuffer';

      let configSent = false;

      this.ws.onopen = async () => {
        console.log('[Publisher] WebSocket connected');

        // Initialize encoders
        await this._initEncoders((videoDesc, audioDesc) => {
          if (configSent || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
          configSent = true;

          const config = {
            videoConfig: {
              codec: STREAM_CONFIG.video.codec.startsWith('avc') ? 'avc1' : STREAM_CONFIG.video.codec,
              codedWidth: this._actualWidth,
              codedHeight: this._actualHeight,
              description: videoDesc ? this._arrayBufferToBase64(videoDesc) : null,
            },
            audioConfig: {
              codec: STREAM_CONFIG.audio.codec,
              sampleRate: STREAM_CONFIG.audio.sampleRate,
              numberOfChannels: STREAM_CONFIG.audio.channels,
              description: audioDesc ? this._arrayBufferToBase64(audioDesc) : null,
            },
          };

          this.ws.send(JSON.stringify(config));
          console.log('[Publisher] Decoder config sent');
          resolve();
        });
      };

      this.ws.onclose = () => {
        console.log('[Publisher] WebSocket closed');
        this.stopPublishing();
      };

      this.ws.onerror = (e) => {
        console.error('[Publisher] WebSocket error:', e);
        reject(new Error('WebSocket connection failed'));
      };
    });
  }

  /**
   * Initialize video and audio encoders
   */
  async _initEncoders(onConfigReady) {
    this.startTimestamp = performance.now();
    this.isPublishing = true;

    let videoDescription = null;
    let sentDecoderConfig = false;

    // Generate AudioSpecificConfig for AAC-LC at 48kHz stereo
    const audioSpecificConfig = new Uint8Array([0x11, 0x90]);

    // Video Encoder
    this.videoEncoder = new VideoEncoder({
      output: (chunk, meta) => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

        const timestamp = Math.floor(performance.now() - this.startTimestamp);
        const isKey = chunk.type === 'key';

        // Capture description from first keyframe (contains SPS/PPS for H.264 or VPS/SPS/PPS for HEVC)
        if (isKey && meta?.decoderConfig?.description && !videoDescription) {
          videoDescription = meta.decoderConfig.description;
          const isHevc = isHevcCodec(STREAM_CONFIG.video.codec);
          const codecType = isHevc ? 'HEVC' : 'H.264';
          const configName = isHevc ? 'hvcC' : 'avcC';
          console.log(`[Publisher] Got ${configName} from ${codecType} encoder:`, videoDescription.byteLength, 'bytes');
        }

        // Send decoder config after we have video description
        if (!sentDecoderConfig && videoDescription) {
          sentDecoderConfig = true;
          onConfigReady(videoDescription, audioSpecificConfig.buffer);
        }

        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);

        const packet = this._createPacket(timestamp, isKey ? 'video-key' : 'video-delta', data);
        this.ws.send(packet);

        this.stats.videoFrames++;
        if (isKey) this.stats.keyframes++;
        this.stats.bytesSent += packet.length;
        this._notifyStats();
      },
      error: (e) => console.error('[Publisher] Video encoder error:', e),
    });

    // Determine codec format options (HEVC vs H.264)
    const isHevc = isHevcCodec(STREAM_CONFIG.video.codec);
    const codecFormatOption = isHevc
      ? { hevc: { format: 'hevc' } }  // For HEVC: get VPS/SPS/PPS
      : { avc: { format: 'avc' } };   // For H.264: get SPS/PPS

    await this.videoEncoder.configure({
      codec: STREAM_CONFIG.video.codec,
      width: this._actualWidth,
      height: this._actualHeight,
      bitrate: STREAM_CONFIG.video.bitrate,
      framerate: STREAM_CONFIG.video.framerate,
      latencyMode: 'realtime',
      ...codecFormatOption,
    });

    console.log(`[Publisher] Video encoder configured: ${isHevc ? 'HEVC' : 'H.264'} ${this._actualWidth}x${this._actualHeight} @ ${STREAM_CONFIG.video.bitrate/1000000}Mbps`);

    // Audio Encoder
    let lastAudioTimestamp = 0;
    this.audioEncoder = new AudioEncoder({
      output: (chunk) => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

        let timestamp = Math.floor(performance.now() - this.startTimestamp);
        if (timestamp <= lastAudioTimestamp) timestamp = lastAudioTimestamp + 1;
        lastAudioTimestamp = timestamp;

        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);

        const packet = this._createPacket(timestamp, 'audio', data);
        this.ws.send(packet);

        this.stats.audioFrames++;
        this.stats.bytesSent += packet.length;
        this._notifyStats();
      },
      error: (e) => console.error('[Publisher] Audio encoder error:', e),
    });

    await this.audioEncoder.configure({
      codec: STREAM_CONFIG.audio.codec,
      sampleRate: STREAM_CONFIG.audio.sampleRate,
      numberOfChannels: STREAM_CONFIG.audio.channels,
      bitrate: STREAM_CONFIG.audio.bitrate,
    });

    console.log('[Publisher] Encoders configured');

    // Start encoding loops
    this._startEncoding();
  }

  /**
   * Start encoding video and audio tracks
   */
  _startEncoding() {
    // Video encoding
    const videoTrack = this.mediaStream.getVideoTracks()[0];
    const videoProcessor = new MediaStreamTrackProcessor({ track: videoTrack });
    const videoReader = videoProcessor.readable.getReader();

    let frameCount = 0;
    const processVideo = async () => {
      while (this.isPublishing) {
        try {
          const { done, value } = await videoReader.read();
          if (done) break;

          if (this.videoEncoder?.state === 'configured') {
            const forceKeyFrame = frameCount % STREAM_CONFIG.video.keyframeInterval === 0;
            this.videoEncoder.encode(value, { keyFrame: forceKeyFrame });
            frameCount++;
          }
          value.close();
        } catch (e) {
          console.error('[Publisher] Video processing error:', e);
          break;
        }
      }
    };
    processVideo();

    // Audio encoding
    const audioTrack = this.mediaStream.getAudioTracks()[0];
    const audioContext = new AudioContext({ sampleRate: STREAM_CONFIG.audio.sampleRate });
    const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
    const processor = audioContext.createScriptProcessor(4096, STREAM_CONFIG.audio.channels, STREAM_CONFIG.audio.channels);

    let audioFrameIndex = 0;
    processor.onaudioprocess = (e) => {
      if (!this.isPublishing || this.audioEncoder?.state !== 'configured') return;

      const left = e.inputBuffer.getChannelData(0);
      const right = e.inputBuffer.getChannelData(1);

      const planarData = new Float32Array(left.length * 2);
      planarData.set(left, 0);
      planarData.set(right, left.length);

      const audioData = new AudioData({
        format: 'f32-planar',
        sampleRate: STREAM_CONFIG.audio.sampleRate,
        numberOfFrames: left.length,
        numberOfChannels: STREAM_CONFIG.audio.channels,
        timestamp: audioFrameIndex * (left.length / STREAM_CONFIG.audio.sampleRate) * 1000000,
        data: planarData,
      });

      this.audioEncoder.encode(audioData);
      audioData.close();
      audioFrameIndex++;
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
    this._audioContext = audioContext;
    this._audioProcessor = processor;
    this._audioSource = source;
  }

  /**
   * Stop publishing
   */
  stopPublishing() {
    this.isPublishing = false;

    if (this.videoEncoder) {
      this.videoEncoder.close();
      this.videoEncoder = null;
    }

    if (this.audioEncoder) {
      this.audioEncoder.close();
      this.audioEncoder = null;
    }

    if (this._audioProcessor) {
      this._audioSource?.disconnect();
      this._audioProcessor?.disconnect();
      this._audioContext?.close();
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }

    if (this.ws) {
      try { this.ws.send('unpublish'); } catch {}
      this.ws.close();
      this.ws = null;
    }

    console.log('[Publisher] Stopped');
  }

  /**
   * Toggle audio mute
   */
  toggleMute() {
    if (this.mediaStream) {
      const audioTracks = this.mediaStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      return !audioTracks[0]?.enabled;
    }
    return false;
  }

  /**
   * Flip camera (front/back)
   */
  async flipCamera() {
    this._facingMode = this._facingMode === 'environment' ? 'user' : 'environment';
    
    if (this.mediaStream) {
      // Stop current video track
      this.mediaStream.getVideoTracks().forEach(track => track.stop());

      // Get new video track
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: STREAM_CONFIG.video.width },
            height: { ideal: STREAM_CONFIG.video.height },
            frameRate: { ideal: STREAM_CONFIG.video.framerate },
            facingMode: this._facingMode,
          },
        });

        const newVideoTrack = newStream.getVideoTracks()[0];
        
        // Replace track in mediaStream
        const oldTrack = this.mediaStream.getVideoTracks()[0];
        if (oldTrack) {
          this.mediaStream.removeTrack(oldTrack);
        }
        this.mediaStream.addTrack(newVideoTrack);

        return this._facingMode;
      } catch (e) {
        console.error('[Publisher] Failed to flip camera:', e);
        // Revert
        this._facingMode = this._facingMode === 'environment' ? 'user' : 'environment';
      }
    }
    return this._facingMode;
  }

  /**
   * Create packet with header
   */
  _createPacket(timestamp, type, data) {
    const payload = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
    const packet = new Uint8Array(5 + payload.length);

    const view = new DataView(packet.buffer);
    view.setUint32(0, Math.max(0, timestamp) >>> 0, false);

    let frameType = 2; // audio
    if (type === 'video-key') frameType = 0;
    else if (type === 'video-delta') frameType = 1;
    packet[4] = frameType;

    packet.set(payload, 5);
    return packet;
  }

  _arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  _notifyStats() {
    if (this.onStats) this.onStats(this.stats);
  }

  /**
   * Get preview video element source
   */
  getPreviewSource() {
    return this.mediaStream;
  }
}

// =============================================================================
// HLS PLAYER
// =============================================================================

// CDN URL for hls.js v1.5.20 (stable version)
const HLS_CDN_URL = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.20/dist/hls.min.js';

/**
 * Dynamically load hls.js from CDN if not already loaded
 */
async function loadHlsJs() {
  if (typeof window.Hls !== 'undefined') {
    return window.Hls;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = HLS_CDN_URL;
    script.async = true;
    script.onload = () => {
      console.log('[HlsPlayer] hls.js loaded from CDN');
      resolve(window.Hls);
    };
    script.onerror = () => {
      reject(new Error('Failed to load hls.js from CDN'));
    };
    document.head.appendChild(script);
  });
}

export class HlsPlayer {
  constructor(videoElement) {
    this.video = videoElement;
    this.hls = null;
    this.onStats = null;
    this._statsInterval = null;
  }

  /**
   * Load HLS stream and wait until ready to play.
   * Does NOT call video.play() — caller must do that explicitly
   * (e.g. inside a click handler so the browser user gesture is preserved).
   */
  async load(hlsUrl) {
    console.log('[Player] Loading HLS:', hlsUrl);

    // Load hls.js from CDN if not available
    await loadHlsJs();

    if (!window.Hls.isSupported()) {
      if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('[Player] Using native HLS (hls.js not supported)');
        this.video.src = hlsUrl;
        return;
      }
      throw new Error('HLS is not supported in this browser');
    }

    this.hls = new window.Hls({
      debug: false,
      enableWorker: true,
      lowLatencyMode: true,
      liveSyncDuration: 1,
      liveMaxLatencyDuration: 3.0,
      maxLiveSyncPlaybackRate: 1.03,
      backBufferLength: 30,
      maxBufferLength: 10,
      maxMaxBufferLength: 30,
      startFragPrefetch: true,
    });

    this.hls.loadSource(hlsUrl);
    this.hls.attachMedia(this.video);

    return new Promise((resolve, reject) => {
      this.hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
        console.log('[Player] Manifest parsed, ready to play');
        this._startStatsUpdate();
        resolve();
      });

      this.hls.on(window.Hls.Events.ERROR, (event, data) => {
        console.error('[Player] Error:', data.type, data.details);
        if (data.fatal) {
          switch (data.type) {
            case window.Hls.ErrorTypes.NETWORK_ERROR:
              console.log('[Player] Fatal network error, attempting recovery...');
              this.hls.startLoad();
              break;
            case window.Hls.ErrorTypes.MEDIA_ERROR:
              console.log('[Player] Fatal media error, attempting recovery...');
              this.hls.recoverMediaError();
              break;
            default:
              reject(new Error(`HLS fatal error: ${data.details}`));
              break;
          }
        }
      });
    });
  }

  /**
   * Stop playback
   */
  stop() {
    if (this._statsInterval) {
      clearInterval(this._statsInterval);
      this._statsInterval = null;
    }

    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }

    this.video.pause();
    this.video.src = '';
    console.log('[Player] Stopped');
  }

  _startStatsUpdate() {
    this._statsInterval = setInterval(() => {
      if (!this.hls || !this.onStats) return;

      const stats = {
        level: this.hls.currentLevel >= 0
          ? this.hls.levels[this.hls.currentLevel]?.height + 'p'
          : '-',
        buffer: this.video.buffered.length > 0
          ? (this.video.buffered.end(this.video.buffered.length - 1) - this.video.currentTime).toFixed(1) + 's'
          : '0s',
        latency: this.hls.latency ? this.hls.latency.toFixed(1) + 's' : '-',
        bandwidth: this.hls.bandwidthEstimate
          ? (this.hls.bandwidthEstimate / 1000000).toFixed(1) + ' Mbps'
          : '-',
      };

      this.onStats(stats);
    }, 1000);
  }
}

// =============================================================================
// SINGLETON INSTANCES
// =============================================================================

// Create singleton API client
const streamApiClient = new StreamApiClient();

export { streamApiClient };
